import { useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Bell, BellOff, Globe, LogOut, Smartphone, Download, Mail, CheckCircle2, AlertCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../auth/useAuth'
import { useInstallPrompt } from '../../shared/hooks/useInstallPrompt'
import { usePushSubscription } from '../../shared/hooks/usePushSubscription'
import { patchMe } from '../../shared/api/auth'
import { getConnectedAccounts, getGoogleConnectUrl, disconnectGoogle } from '../../shared/api/gmail'
import { Button } from '../../shared/components/Button'

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'ru', label: 'Русский' },
  { code: 'he', label: 'עברית' },
]

export function Settings() {
  const { t, i18n } = useTranslation()
  const { user, logout } = useAuth()
  const { canInstall, promptInstall } = useInstallPrompt()
  const push = usePushSubscription()
  const qc = useQueryClient()
  const [searchParams] = useSearchParams()

  const { data: accounts = [] } = useQuery({
    queryKey: ['connected-accounts'],
    queryFn: getConnectedAccounts,
  })

  const disconnectMutation = useMutation({
    mutationFn: disconnectGoogle,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['connected-accounts'] }),
  })

  const googleAccount = accounts.find((a) => a.provider === 'google')
  const gmailConnected = googleAccount?.connected ?? false

  // Handle ?connected=gmail redirect back from OAuth callback
  useEffect(() => {
    if (searchParams.get('connected') === 'gmail') {
      void qc.invalidateQueries({ queryKey: ['connected-accounts'] })
    }
  }, [searchParams, qc])

  function changeLanguage(code: string) {
    void i18n.changeLanguage(code)
    try { localStorage.setItem('smart-travel:lang', code) } catch { /* ignore */ }
    void patchMe({ language: code }).catch(() => { /* best-effort */ })
  }

  return (
    <div className="mx-auto max-w-xl pb-12">
      <Link
        to="/"
        className="mb-4 inline-flex items-center gap-1 text-sm text-st-muted hover:text-st-text"
      >
        <ArrowLeft className="h-4 w-4" /> {t('common.back_to_trips')}
      </Link>

      <h1 className="font-display text-2xl font-bold text-st-text">{t('settings.title')}</h1>

      <section className="mt-6 rounded-st-lg border border-st-line bg-st-surface p-4">
        <h2 className="mb-3 text-sm font-semibold text-st-text">{t('settings.account')}</h2>
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-st-full bg-st-surface-2 text-base font-semibold text-st-primary">
            {user?.name?.charAt(0) ?? '?'}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-st-text">{user?.name ?? 'Guest'}</p>
            <p className="truncate text-xs text-st-muted">{user?.email}</p>
          </div>
        </div>
      </section>

      <section className="mt-4 rounded-st-lg border border-st-line bg-st-surface p-4">
        <h2 className="mb-3 text-sm font-semibold text-st-text">{t('settings.preferences')}</h2>
        <Row
          Icon={Globe}
          label={t('settings.language')}
          action={
            <div className="flex gap-1">
              {LANGUAGES.map(({ code, label }) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => changeLanguage(code)}
                  className={`rounded-st-sm px-2 py-1 text-xs font-medium transition ${
                    i18n.language === code
                      ? 'bg-st-primary text-white'
                      : 'border border-st-line text-st-muted hover:border-st-primary hover:text-st-primary'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          }
        />
        <Row
          Icon={Smartphone}
          label={t('settings.install_app')}
          value={canInstall ? '' : t('settings.install_browser_hint')}
          action={
            canInstall ? (
              <Button variant="outline" onClick={() => void promptInstall()}>
                <Download className="h-4 w-4" /> {t('settings.install')}
              </Button>
            ) : undefined
          }
        />
        {push.supported && (
          <Row
            Icon={push.subscribed ? Bell : BellOff}
            label={t('settings.reminders')}
            hint={push.subscribed ? t('settings.reminders_on') : t('settings.reminders_hint')}
            action={
              <Button
                variant="outline"
                onClick={() => void (push.subscribed ? push.unsubscribe() : push.subscribe())}
              >
                {push.subscribed ? t('settings.turn_off') : t('settings.enable')}
              </Button>
            }
          />
        )}
        {push.error && <p className="mt-1 text-xs text-red-600">{push.error}</p>}
      </section>

      {/* Connected accounts */}
      <section className="mt-4 rounded-st-lg border border-st-line bg-st-surface p-4">
        <h2 className="mb-3 text-sm font-semibold text-st-text">Connected accounts</h2>
        <Row
          Icon={Mail}
          label="Google / Gmail"
          hint={
            gmailConnected
              ? 'Gmail is connected — you can import emails into your trips.'
              : 'Connect to import flight & hotel confirmations from Gmail.'
          }
          action={
            gmailConnected ? (
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" aria-label="Connected" />
                <Button
                  variant="ghost"
                  onClick={() => disconnectMutation.mutate()}
                  disabled={disconnectMutation.isPending}
                  className="text-st-danger text-xs"
                >
                  Disconnect
                </Button>
              </div>
            ) : (
              <a href={getGoogleConnectUrl()}>
                <Button variant="outline">
                  <AlertCircle className="h-4 w-4" /> Connect
                </Button>
              </a>
            )
          }
        />
      </section>

      <section className="mt-4">
        <Button variant="ghost" onClick={() => void logout()} className="text-st-danger">
          <LogOut className="h-4 w-4" /> {t('settings.sign_out')}
        </Button>
      </section>
    </div>
  )
}

function Row({
  Icon,
  label,
  value,
  hint,
  action,
}: {
  Icon: typeof Globe
  label: string
  value?: string
  hint?: string
  action?: React.ReactNode
}) {
  return (
    <div className="flex items-center gap-3 border-t border-st-line py-3 first:border-t-0">
      <Icon className="h-4.5 w-4.5 text-st-muted" />
      <div className="min-w-0 flex-1">
        <p className="text-sm text-st-text">{label}</p>
        {hint && <p className="text-xs text-st-muted">{hint}</p>}
      </div>
      {action ?? (value ? <span className="text-sm text-st-muted">{value}</span> : null)}
    </div>
  )
}
